import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {
  acceptDemoInvite,
  declineDemoInvite,
  demoFamilyConfirmResult,
  demoFamilyLinks,
  demoFamilyPreview,
  demoResolveRequest,
  generateDemoCode,
  getDemoFamilyState,
  revokeDemoLink,
} from '#/lib/demoFamily'
import {BSKY_APP_HOST} from '#/lib/strings/url-helpers'
import {STALE} from '#/state/queries'
import {useAgent} from '#/state/session'

export const FAMILY_LINKS_RQKEY = 'familyLinks'

/** The shareable deep link encoded in the QR / sent over chat. */
export function makeFamilyInviteLink(code: string): string {
  return `${BSKY_APP_HOST}/family/${code}`
}

/** Extracts the invite code from a https://…/family/<code> URL. */
export function familyInviteCodeFromUri(uri: string): string | undefined {
  try {
    const url = new URL(uri)
    const match = /^\/family\/([^/?#]+)$/.exec(url.pathname)
    return match?.[1] ?? undefined
  } catch {
    return undefined
  }
}

/**
 * Scans message/record facets for a link pointing at /family/:code — used to
 * render the family invite card under a DM bubble.
 */
export function familyInviteCodeFromFacets(
  facets: unknown[] | undefined,
): string | undefined {
  if (!facets) return undefined
  for (const facet of facets) {
    const features = (facet as {features?: Array<{uri?: string}>})?.features
    for (const feature of features ?? []) {
      if (feature.uri) {
        const code = familyInviteCodeFromUri(feature.uri)
        if (code) return code
      }
    }
  }
  return undefined
}

export type FamilyLinkDirection = 'parent' | 'child'

export type FamilyLinkInfo = {
  peerDid: string
  createdAt: string
}

export type FamilyLinks = {
  asParent: FamilyLinkInfo[]
  asChild: FamilyLinkInfo[]
}

type ResolvedRequest = {
  initiatorDid: string
  initiatorRole: 'parent' | 'child'
  createdAt: string
  expiresAt: string
  alreadyUsed?: boolean
}

async function authFetch<T>(
  agent: ReturnType<typeof useAgent>,
  nsid: string,
  opts: {params?: Record<string, unknown>; init?: RequestInit} = {},
): Promise<T> {
  const accessJwt = agent.session?.accessJwt
  if (!accessJwt) throw new Error('Not authenticated')

  const base = agent.serviceUrl.toString().replace(/\/+$/, '')
  const url = new URL(`${base}/xrpc/${nsid}`)
  if (opts.params) {
    for (const [key, value] of Object.entries(opts.params)) {
      if (value === undefined) continue
      url.searchParams.set(key, String(value))
    }
  }
  const res = await fetch(url.toString(), {
    ...opts.init,
    headers: {
      Authorization: `Bearer ${accessJwt}`,
      ...(opts.init?.body ? {'Content-Type': 'application/json'} : {}),
      ...(opts.init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    const message = (err as {message?: string; error?: string}).message
    const error = (err as {error?: string}).error
    const e = new Error(message ?? `HTTP ${res.status}`) as Error & {
      error?: string
    }
    if (error) e.error = error
    throw e
  }
  return res.json() as Promise<T>
}

export function useFamilyLinksQuery() {
  const agent = useAgent()
  const did = agent.session?.did
  return useQuery({
    queryKey: [FAMILY_LINKS_RQKEY, did ?? ''],
    enabled: !!did,
    staleTime: STALE.SECONDS.THIRTY,
    queryFn: async (): Promise<FamilyLinks> => {
      // Demo mode: simulate linked members locally (no backend needed).
      if (getDemoFamilyState().enabled) {
        return demoFamilyLinks()
      }
      const raw = await authFetch<{asParent?: unknown[]; asChild?: unknown[]}>(
        agent,
        'app.bsky.family.getFamilyLinks',
      )
      const map = (arr: unknown[] | undefined): FamilyLinkInfo[] =>
        (arr ?? []).map(item => {
          const it = item as {
            parentDid?: string
            childDid?: string
            createdAt?: string
          }
          return {
            peerDid: it.parentDid ?? it.childDid ?? '',
            createdAt: it.createdAt ?? '',
          }
        })
      return {
        asParent: map(raw.asParent),
        asChild: map(raw.asChild),
      }
    },
  })
}

export function useCreateFamilyLinkRequest() {
  const agent = useAgent()
  return useMutation({
    mutationFn: async (initiatorRole: FamilyLinkDirection) => {
      // Demo mode: mint a local code so the QR + share flow is fully visible.
      if (getDemoFamilyState().enabled) {
        const code = generateDemoCode()
        return {
          code,
          expiresAt: new Date(Date.now() + 10 * 60_000).toISOString(),
        }
      }
      return authFetch<{code: string; expiresAt: string}>(
        agent,
        'app.bsky.family.createFamilyLinkRequest',
        {
          init: {
            method: 'POST',
            body: JSON.stringify({initiatorRole}),
          },
        },
      )
    },
  })
}

export function useResolveFamilyLinkRequest() {
  const agent = useAgent()
  return useMutation({
    mutationFn: async (code: string): Promise<ResolvedRequest> => {
      // Demo mode: any code resolves to the simulated initiator.
      const demo = demoResolveRequest(code, {
        did: agent.session?.did,
        handle: agent.session?.handle,
      })
      if (demo) {
        return demo
      }
      return authFetch(agent, 'app.bsky.family.resolveFamilyLinkRequest', {
        params: {code},
      })
    },
  })
}

export function useDeclineFamilyLink() {
  const agent = useAgent()
  return useMutation({
    mutationFn: async (input: {
      code: string
      reason: string
      note?: string
    }) => {
      // Demo mode: record the decline locally (card + feedback message). The
      // real endpoint isn't shipped to the PDS yet, so non-demo calls surface
      // a friendly MethodNotImplemented error until the backend deploys.
      if (getDemoFamilyState().enabled) {
        declineDemoInvite(input.reason)
        return {declined: true}
      }
      return authFetch<{declined: boolean}>(
        agent,
        'app.bsky.family.declineFamilyLink',
        {
          init: {
            method: 'POST',
            body: JSON.stringify({
              code: input.code,
              reason: input.reason,
              note: input.note,
            }),
          },
        },
      )
    },
  })
}

export function useConfirmFamilyLink() {
  const agent = useAgent()
  const queryClient = useQueryClient()
  const did = agent.session?.did
  return useMutation({
    mutationFn: async (code: string) => {
      // Demo mode: accept locally, then the normal onSuccess invalidates the
      // links query so the new member appears in the list.
      if (getDemoFamilyState().enabled) {
        acceptDemoInvite()
        return demoFamilyConfirmResult(agent.session?.did)
      }
      return authFetch<{parentDid: string; childDid: string; createdAt: string}>(
        agent,
        'app.bsky.family.confirmFamilyLink',
        {
          init: {method: 'POST', body: JSON.stringify({code})},
        },
      )
    },
    onSuccess: () => {
      if (!did) return
      void queryClient.invalidateQueries({queryKey: [FAMILY_LINKS_RQKEY, did]})
    },
  })
}

export function useRevokeFamilyLink() {
  const agent = useAgent()
  const queryClient = useQueryClient()
  const did = agent.session?.did
  return useMutation({
    mutationFn: async (peerDid: string) => {
      if (getDemoFamilyState().enabled) {
        revokeDemoLink()
        return {revoked: true}
      }
      return authFetch<{revoked: boolean}>(
        agent,
        'app.bsky.family.revokeFamilyLink',
        {
          init: {method: 'POST', body: JSON.stringify({peerDid})},
        },
      )
    },
    onSuccess: () => {
      if (!did) return
      void queryClient.invalidateQueries({queryKey: [FAMILY_LINKS_RQKEY, did]})
    },
  })
}

export type FamilyLinkPreview = {
  initiatorDid: string
  initiatorRole: 'parent' | 'child'
  createdAt: string
  expiresAt: string
  alreadyUsed?: boolean
  initiator: {
    did: string
    handle: string
    displayName?: string
    avatar?: string
  }
}

/**
 * Public (no-auth) resolution of a family invite code, used by the invite
 * landing screen, the DM card, and the logged-out preview. Calls the appview
 * directly because the lexicon isn't in the published @atproto/api yet.
 */
export function useFamilyLinkPreviewQuery(code: string | undefined) {
  const agent = useAgent()
  return useQuery({
    queryKey: ['familyLinkPreview', code ?? ''],
    enabled: !!code,
    staleTime: STALE.MINUTES.ONE,
    queryFn: async (): Promise<FamilyLinkPreview> => {
      // Demo mode: resolve locally (our code → us, anything else → demo peer).
      const demo = demoFamilyPreview(code!, {
        did: agent.session?.did,
        handle: agent.session?.handle,
      })
      if (demo) {
        // Our own invitation: fill the card with the real logged-in profile
        // (display name + avatar) instead of the handle placeholder, so the
        // invite card and share-link preview are dynamic — driven by the
        // actual account — rather than a static demo.
        if (agent.session?.did && demo.initiatorDid === agent.session.did) {
          try {
            const {data: profile} = await agent.getProfile({
              actor: agent.session.did,
            })
            if (profile) {
              return {
                ...demo,
                initiator: {
                  ...demo.initiator,
                  displayName:
                    profile.displayName || demo.initiator.displayName,
                  avatar: profile.avatar ?? demo.initiator.avatar,
                },
              }
            }
          } catch {
            // Profile lookup is best-effort — keep the demo fallback.
          }
        }
        return demo
      }
      const base = agent.serviceUrl.toString().replace(/\/+$/, '')
      const url = new URL(`${base}/xrpc/app.bsky.family.getFamilyLinkPreview`)
      url.searchParams.set('code', code!)
      // optionalStandardOrRole on the server; attach the token when we have one
      // so PDS proxies that gate app.* methods on auth still resolve it.
      const headers: Record<string, string> = {}
      if (agent.session?.accessJwt) {
        headers.Authorization = `Bearer ${agent.session.accessJwt}`
      }
      const res = await fetch(url.toString(), {headers})
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        const message = (err as {message?: string}).message
        const error = (err as {error?: string}).error
        const e = new Error(message ?? `HTTP ${res.status}`) as Error & {
          error?: string
        }
        if (error) e.error = error
        throw e
      }
      return res.json() as Promise<FamilyLinkPreview>
    },
  })
}

export type MemberPlay = {
  id: string
  capsuleUri: string
  score: number
  timeMs: number
  status: 'started' | 'completed'
  createdAt: string
}

/** Reads the plays of a linked child (parent-scoped). */
export function useFamilyMemberPlaysQuery(childDid: string | undefined) {
  const agent = useAgent()
  return useQuery({
    queryKey: ['familyMemberPlays', childDid ?? ''],
    enabled: !!childDid,
    staleTime: STALE.MINUTES.ONE,
    queryFn: async (): Promise<MemberPlay[]> => {
      // Demo mode: no real plays exist for the demo child; the Family screen
      // renders a simulated progress summary instead.
      if (getDemoFamilyState().enabled) {
        return []
      }
      const raw = await authFetch<{plays?: MemberPlay[]}>(
        agent,
        'app.bsky.family.getFamilyMemberPlays',
        {params: {actor: childDid, limit: 50}},
      )
      return raw.plays ?? []
    },
  })
}
